import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import './EmailPreview.scss';

const SHOPMY_LOGO_ON_DARK = 'https://static.shopmy.us/Logo/shopmy_white_2025.png';
const EMAIL_WIDTH = 640;
const BODY_PADDING = 18;
const COLLAPSED_HEIGHT = 420;

// A backend email as the inbox shows it: the sender, the subject, the preview text, and the body the
// server rendered, from the template or from the body drafted in the Registry. The body lays out at
// desktop width and scales to the tile; a long one is capped until opened.
const EmailPreview = ({ subject, previewText, html, source }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [naturalHeight, setNaturalHeight] = useState(COLLAPSED_HEIGHT);
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc || !html) return;
    const scale = Math.min(1, iframe.clientWidth / EMAIL_WIDTH);
    doc.open();
    doc.write(
      `<html><head><style>html { overflow: hidden; background: #f1f1f1; } body { margin: ${BODY_PADDING}px ${
        scale < 1 ? 0 : 'auto'
      }; width: ${EMAIL_WIDTH}px; transform-origin: top left; transform: scale(${scale}); font-family: sans-serif; }</style></head><body>${html}</body></html>`
    );
    doc.close();
    const measure = () => setNaturalHeight(Math.ceil(doc.body.offsetHeight * scale) + BODY_PADDING * 2);
    measure();
    doc.querySelectorAll('img').forEach(img => img.addEventListener('load', measure));
    setIsOpen(false);
  }, [html]);

  if (!html) return null;

  const isCapped = naturalHeight > COLLAPSED_HEIGHT;
  const height = isCapped && !isOpen ? COLLAPSED_HEIGHT : naturalHeight;
  return (
    <div className='email-preview-outer'>
      <div className='mail-head'>
        <div className='from-mark'>
          <img src={SHOPMY_LOGO_ON_DARK} alt='' />
        </div>
        <div className='from'>
          ShopMy <span>team@shopmy.us</span>
        </div>
        <div className='when'>9:41 AM</div>
        <div className='subject'>{subject || 'No subject'}</div>
        {previewText && <div className='preview-text'>{previewText}</div>}
      </div>
      <div className={cn('mail-body', { capped: isCapped && !isOpen })} style={{ height }}>
        <iframe ref={iframeRef} title='Email preview' sandbox='allow-same-origin' scrolling='no' className='email-iframe' />
      </div>
      <div className='mail-foot'>
        <span>{source === 'template' ? 'From the template' : 'From the body drafted here, in the standard ShopMy layout'} · desktop</span>
        {isCapped && (
          <span className='toggle' onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? 'Collapse' : 'Show the whole email'}
          </span>
        )}
      </div>
    </div>
  );
};

EmailPreview.propTypes = {
  subject: PropTypes.string,
  previewText: PropTypes.string,
  html: PropTypes.string,
  source: PropTypes.oneOf(['template', 'drafted']).isRequired
};

export default EmailPreview;
